//src/components/steps/edit/EditStep1PropertyDetail.jsx
import { useQuery } from '@tanstack/react-query';
import { Editor } from '@tinymce/tinymce-react';
import { useEffect, useState } from 'react';
import { BsHouseAdd } from 'react-icons/bs';
import {
    MdBathtub,
    MdBed,
    MdCalendarMonth,
    MdDirectionsCar,
    MdElectricBolt,
    MdLayers,
    MdLocationOn,
    MdOutlineHomeWork,
    MdOutlineVerified,
    MdSquareFoot,
    MdTipsAndUpdates,
} from 'react-icons/md';
import { RiBuilding2Line } from 'react-icons/ri';
import { TbBuildingEstate } from 'react-icons/tb';
import { useEditProperty } from '../../EditPropertyContext';
import LocationDropdown from '../../SellProperty/LocationDropdown';
import { getDistricts, getProvinces, getRegencies, getVillages } from '../../SellProperty/locationApi';
import { FormField, InputIcon, StepSection } from '../../SellPropertyUI';
import {
    BUILDING_CONDITION_OPTIONS,
    BUILDING_ORIENTATION_OPTIONS,
    FURNISHING_STATUS_OPTIONS,
    LISTING_TYPE,
} from '../../sellProperty.constants';
import { openPropertyTitleTipsModal } from '../PropertyTitleTipsModal';

const TINYMCE_API_KEY = process.env.REACT_APP_TINYMCE_API_KEY;

const TINYMCE_INIT = {
    height: 280,
    menubar: false,
    branding: false,
    statusbar: false,
    resize: false,
    plugins: [
        'advlist',
        'autolink',
        'lists',
        'link',
        'charmap',
        'preview',
        'anchor',
        'searchreplace',
        'visualblocks',
        'code',
        'insertdatetime',
        'table',
        'wordcount',
    ],
    toolbar: 'undo redo | bold italic underline | bullist numlist | removeformat',
    content_style:
        "body { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; color: #1a2340; line-height: 1.65; }",
    placeholder: 'Ceritakan keunggulan, kondisi, fasilitas dan keistimewaan properti Anda...',
};

const PROPERTY_TYPE_ICONS = {
    default: <MdOutlineHomeWork size={22} />,
    apartemen: <RiBuilding2Line size={22} />,
    ruko: <TbBuildingEstate size={22} />,
    tanah: <MdSquareFoot size={22} />,
    villa: <BsHouseAdd size={22} />,
};

const RequiredMark = () => <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>;

const EditStep1PropertyDetail = () => {
    const { form, errors, handleChange, propertyTypes, certificateTypes, loadingData, isTanah, setFieldValue } =
        useEditProperty();

    const [provinceCode, setProvinceCode] = useState('');
    const [regencyCode, setRegencyCode] = useState('');
    const [districtCode, setDistrictCode] = useState('');

    // For edit mode, we show the current values as text in dropdowns
    // The user can change them; the codes will be populated when they select new ones
    const [locationInitialized, setLocationInitialized] = useState(false);

    const provincesQuery = useQuery({
        queryKey: ['location', 'provinces'],
        queryFn: getProvinces,
        staleTime: 24 * 60 * 60 * 1000,
        gcTime: 7 * 24 * 60 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: 1,
    });

    const regenciesQuery = useQuery({
        queryKey: ['location', 'regencies', provinceCode],
        queryFn: () => getRegencies(provinceCode),
        enabled: !!provinceCode,
        staleTime: 24 * 60 * 60 * 1000,
        gcTime: 7 * 24 * 60 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: 1,
    });

    const districtsQuery = useQuery({
        queryKey: ['location', 'districts', regencyCode],
        queryFn: () => getDistricts(regencyCode),
        enabled: !!regencyCode,
        staleTime: 24 * 60 * 60 * 1000,
        gcTime: 7 * 24 * 60 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: 1,
    });

    const villagesQuery = useQuery({
        queryKey: ['location', 'villages', districtCode],
        queryFn: () => getVillages(districtCode),
        enabled: !!districtCode,
        staleTime: 24 * 60 * 60 * 1000,
        gcTime: 7 * 24 * 60 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: 1,
    });

    // When provinces are loaded and we have an existing province, auto-find the code
    useEffect(() => {
        if (locationInitialized) return;
        if (!provincesQuery.data?.length || !form.province) return;

        const found = provincesQuery.data.find((p) => p.name?.toLowerCase() === form.province?.toLowerCase());
        if (found) {
            setProvinceCode(found.code);
            setLocationInitialized(true);
        }
    }, [provincesQuery.data, form.province, locationInitialized]);

    // After province code is set, auto-find regency code
    useEffect(() => {
        if (!regenciesQuery.data?.length || !form.city || regencyCode) return;

        const found = regenciesQuery.data.find((r) => r.name?.toLowerCase() === form.city?.toLowerCase());
        if (found) setRegencyCode(found.code);
    }, [regenciesQuery.data, form.city, regencyCode]);

    // After regency code is set, auto-find district code
    useEffect(() => {
        if (!districtsQuery.data?.length || !form.district || districtCode) return;

        const found = districtsQuery.data.find((d) => d.name?.toLowerCase() === form.district?.toLowerCase());
        if (found) setDistrictCode(found.code);
    }, [districtsQuery.data, form.district, districtCode]);

    const handleStatusChange = (value) => setFieldValue('status', value);

    const handleProvinceSelect = (item) => {
        setProvinceCode(item.code);
        setRegencyCode('');
        setDistrictCode('');
        setFieldValue('province', item.name);
        setFieldValue('city', '');
        setFieldValue('district', '');
        setFieldValue('village', '');
    };

    const handleCitySelect = (item) => {
        setRegencyCode(item.code);
        setDistrictCode('');
        setFieldValue('city', item.name);
        setFieldValue('district', '');
        setFieldValue('village', '');
    };

    const handleDistrictSelect = (item) => {
        setDistrictCode(item.code);
        setFieldValue('district', item.name);
        setFieldValue('village', '');
    };

    const handleVillageSelect = (item) => setFieldValue('village', item.name);

    const handleDescriptionChange = (content) => setFieldValue('description', content);

    return (
        <StepSection
            icon={<MdOutlineHomeWork size={20} color="var(--color-primary)" />}
            title="Detail Properti"
            desc="Perbarui informasi lengkap tentang properti Anda."
        >
            {/* Listing Type */}
            <div className="sell-option-group">
                <label className="mp-label">
                    Jenis Listing <RequiredMark />
                </label>
                <div className="sell-option-row">
                    {LISTING_TYPE.map((t) => {
                        const Icon = t.icon;
                        return (
                            <button
                                key={t.value}
                                type="button"
                                className={`sell-option-btn ${form.status === t.value ? 'active' : ''}`}
                                onClick={() => handleStatusChange(t.value)}
                            >
                                <span className="sell-option-icon">
                                    <Icon size={18} />
                                </span>
                                {t.label}
                            </button>
                        );
                    })}
                </div>
                {errors.status && <span className="sell-field-error">{errors.status}</span>}
            </div>

            {/* Property Type */}
            <div className="sell-option-group">
                <label className="mp-label">
                    Tipe Properti <RequiredMark />
                </label>
                {loadingData ? (
                    <p className="sell-loading-text">Memuat tipe properti...</p>
                ) : (
                    <div className="sell-type-grid">
                        {propertyTypes.map((t) => {
                            const icon = PROPERTY_TYPE_ICONS[t.name?.toLowerCase()] || PROPERTY_TYPE_ICONS.default;
                            return (
                                <button
                                    key={t.id}
                                    type="button"
                                    className={`sell-type-btn${String(form.property_type_id) === String(t.id) ? ' active' : ''}`}
                                    onClick={() => setFieldValue('property_type_id', String(t.id))}
                                >
                                    {icon}
                                    {t.name}
                                </button>
                            );
                        })}
                    </div>
                )}
                {errors.property_type_id && <span className="sell-field-error">{errors.property_type_id}</span>}
            </div>

            <div className="sell-grid">
                {/* Title */}
                <FormField
                    label={
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 12,
                                flexWrap: 'wrap',
                                width: '100%',
                            }}
                        >
                            <span>
                                Judul Iklan <RequiredMark />
                            </span>
                            <button
                                type="button"
                                onClick={openPropertyTitleTipsModal}
                                style={{
                                    border: 'none',
                                    background: 'transparent',
                                    color: 'var(--color-primary)',
                                    fontWeight: 700,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: 0,
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <MdTipsAndUpdates size={16} />
                                Lihat Tips
                            </button>
                        </div>
                    }
                    fullWidth
                    error={errors.title}
                >
                    <input
                        className={`mp-input${errors.title ? ' error' : ''}`}
                        type="text"
                        name="title"
                        placeholder="Contoh: Rumah Minimalis 2 Lantai di Bandung Selatan"
                        value={form.title}
                        onChange={handleChange}
                    />
                </FormField>

                {/* Address */}
                <FormField
                    label={
                        <>
                            Alamat Properti <RequiredMark />
                        </>
                    }
                    fullWidth
                    error={errors.address}
                >
                    <div className="sell-input-wrap">
                        <InputIcon>
                            <MdLocationOn size={16} />
                        </InputIcon>
                        <input
                            className={`mp-input sell-input-padded${errors.address ? ' error' : ''}`}
                            type="text"
                            name="address"
                            placeholder="Jalan, No. Rumah, RT/RW, Kelurahan"
                            value={form.address}
                            onChange={handleChange}
                        />
                    </div>
                </FormField>

                {/* Location dropdowns */}
                <div className="sell-location-grid sell-field--full">
                    <LocationDropdown
                        label="Provinsi"
                        required
                        value={form.province}
                        placeholder="Pilih provinsi"
                        searchPlaceholder="Cari provinsi..."
                        options={provincesQuery.data || []}
                        loading={provincesQuery.isLoading || provincesQuery.isFetching}
                        disabled={provincesQuery.isError}
                        error={errors.province}
                        onSelect={handleProvinceSelect}
                    />
                    <LocationDropdown
                        label="Kota / Kabupaten"
                        required
                        value={form.city}
                        placeholder={form.province ? 'Pilih kota/kabupaten' : 'Pilih provinsi dulu'}
                        searchPlaceholder="Cari kota/kabupaten..."
                        options={regenciesQuery.data || []}
                        loading={regenciesQuery.isLoading || regenciesQuery.isFetching}
                        disabled={!provinceCode || regenciesQuery.isError}
                        error={errors.city}
                        onSelect={handleCitySelect}
                    />
                    <LocationDropdown
                        label="Kecamatan"
                        required
                        value={form.district}
                        placeholder={form.city ? 'Pilih kecamatan' : 'Pilih kota/kabupaten dulu'}
                        searchPlaceholder="Cari kecamatan..."
                        options={districtsQuery.data || []}
                        loading={districtsQuery.isLoading || districtsQuery.isFetching}
                        disabled={!regencyCode || districtsQuery.isError}
                        error={errors.district}
                        onSelect={handleDistrictSelect}
                    />
                    <LocationDropdown
                        label="Desa"
                        required
                        value={form.village}
                        placeholder={form.district ? 'Pilih desa' : 'Pilih kecamatan dulu'}
                        searchPlaceholder="Cari desa..."
                        options={villagesQuery.data || []}
                        loading={villagesQuery.isLoading || villagesQuery.isFetching}
                        disabled={!districtCode || villagesQuery.isError}
                        error={errors.village}
                        onSelect={handleVillageSelect}
                    />
                </div>

                {/* Location / Area */}
                <FormField label="Lokasi / Area">
                    <input
                        className="mp-input"
                        type="text"
                        name="location"
                        placeholder="Contoh: Bandung Selatan"
                        value={form.location}
                        onChange={handleChange}
                    />
                </FormField>

                {/* Certificate */}
                <FormField label="Sertifikat Properti">
                    <div className="sell-input-wrap">
                        <InputIcon>
                            <MdOutlineVerified size={16} />
                        </InputIcon>
                        <select
                            className="mp-input sell-input-padded"
                            name="certificate_type_id"
                            value={form.certificate_type_id}
                            onChange={handleChange}
                        >
                            <option value="">Pilih sertifikat</option>
                            {certificateTypes.map((item) => (
                                <option key={item.id} value={item.id}>
                                    {item.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </FormField>

                {/* Non-land fields */}
                {!isTanah && (
                    <>
                        <FormField label="Tahun Dibangun">
                            <div className="sell-input-wrap">
                                <InputIcon>
                                    <MdCalendarMonth size={16} />
                                </InputIcon>
                                <input
                                    className="mp-input sell-input-padded"
                                    type="number"
                                    name="construction_year"
                                    placeholder="Contoh: 2018"
                                    value={form.construction_year}
                                    onChange={handleChange}
                                    min="1900"
                                    max={new Date().getFullYear()}
                                />
                            </div>
                        </FormField>

                        <FormField label="Tahun Renovasi">
                            <div className="sell-input-wrap">
                                <InputIcon>
                                    <MdCalendarMonth size={16} />
                                </InputIcon>
                                <input
                                    className="mp-input sell-input-padded"
                                    type="number"
                                    name="renovation_year"
                                    placeholder="Contoh: 2023"
                                    value={form.renovation_year}
                                    onChange={handleChange}
                                    min="1900"
                                    max={new Date().getFullYear()}
                                />
                            </div>
                        </FormField>

                        <FormField label="Kamar Tidur">
                            <div className="sell-input-wrap">
                                <InputIcon>
                                    <MdBed size={16} />
                                </InputIcon>
                                <input
                                    className="mp-input sell-input-padded"
                                    type="number"
                                    name="bedrooms"
                                    placeholder="Jumlah kamar tidur"
                                    value={form.bedrooms}
                                    onChange={handleChange}
                                    min="0"
                                />
                            </div>
                        </FormField>

                        <FormField label="Kamar Mandi">
                            <div className="sell-input-wrap">
                                <InputIcon>
                                    <MdBathtub size={16} />
                                </InputIcon>
                                <input
                                    className="mp-input sell-input-padded"
                                    type="number"
                                    name="bathrooms"
                                    placeholder="Jumlah kamar mandi"
                                    value={form.bathrooms}
                                    onChange={handleChange}
                                    min="0"
                                />
                            </div>
                        </FormField>

                        <FormField label="Jumlah Lantai">
                            <div className="sell-input-wrap">
                                <InputIcon>
                                    <MdLayers size={16} />
                                </InputIcon>
                                <input
                                    className="mp-input sell-input-padded"
                                    type="number"
                                    name="floors"
                                    placeholder="Contoh: 2"
                                    value={form.floors}
                                    onChange={handleChange}
                                    min="0"
                                />
                            </div>
                        </FormField>

                        <FormField label="Garasi (mobil)">
                            <div className="sell-input-wrap">
                                <InputIcon>
                                    <MdDirectionsCar size={16} />
                                </InputIcon>
                                <input
                                    className="mp-input sell-input-padded"
                                    type="number"
                                    name="garage_count"
                                    placeholder="Contoh: 1"
                                    value={form.garage_count}
                                    onChange={handleChange}
                                    min="0"
                                />
                            </div>
                        </FormField>

                        <FormField label="Carport (mobil)">
                            <div className="sell-input-wrap">
                                <InputIcon>
                                    <MdDirectionsCar size={16} />
                                </InputIcon>
                                <input
                                    className="mp-input sell-input-padded"
                                    type="number"
                                    name="carport_count"
                                    placeholder="Contoh: 1"
                                    value={form.carport_count}
                                    onChange={handleChange}
                                    min="0"
                                />
                            </div>
                        </FormField>

                        <FormField label="Kondisi Perabotan">
                            <select
                                className="mp-input"
                                name="furnishing_status"
                                value={form.furnishing_status}
                                onChange={handleChange}
                            >
                                {FURNISHING_STATUS_OPTIONS.map((opt) => (
                                    <option key={opt.value || 'placeholder'} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </FormField>

                        <FormField label="Daya Listrik">
                            <div className="sell-input-wrap">
                                <InputIcon>
                                    <MdElectricBolt size={16} />
                                </InputIcon>
                                <input
                                    className="mp-input sell-input-padded"
                                    type="number"
                                    name="electric_power"
                                    placeholder="Contoh: 3500"
                                    value={form.electric_power}
                                    onChange={handleChange}
                                    min="0"
                                />
                            </div>
                        </FormField>

                        <FormField label="Hadap Bangunan">
                            <select
                                className="mp-input"
                                name="building_orientation"
                                value={form.building_orientation}
                                onChange={handleChange}
                            >
                                {BUILDING_ORIENTATION_OPTIONS.map((opt) => (
                                    <option key={opt.value || 'placeholder'} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </FormField>

                        <FormField label="Kondisi Bangunan">
                            <select
                                className="mp-input"
                                name="building_condition"
                                value={form.building_condition}
                                onChange={handleChange}
                            >
                                {BUILDING_CONDITION_OPTIONS.map((opt) => (
                                    <option key={opt.value || 'placeholder'} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </FormField>

                        <FormField label="Luas Bangunan (m²)">
                            <input
                                className="mp-input"
                                type="number"
                                name="building_area"
                                placeholder="Contoh: 90"
                                value={form.building_area}
                                onChange={handleChange}
                                min="0"
                            />
                        </FormField>
                    </>
                )}

                <FormField label="Luas Tanah (m²)">
                    <input
                        className="mp-input"
                        type="number"
                        name="land_area"
                        placeholder="Contoh: 120"
                        value={form.land_area}
                        onChange={handleChange}
                        min="0"
                    />
                </FormField>

                <FormField label="Deskripsi Properti" fullWidth error={errors.description}>
                    <div className={`tinymce-wrapper${errors.description ? ' tinymce-wrapper--error' : ''}`}>
                        <Editor
                            apiKey={TINYMCE_API_KEY}
                            value={form.description}
                            onEditorChange={handleDescriptionChange}
                            init={TINYMCE_INIT}
                        />
                    </div>
                </FormField>
            </div>
        </StepSection>
    );
};

export default EditStep1PropertyDetail;
